/*
 * Copyright Innov'ATM all rights reserved.
 * This software is the property of Innov'ATM and may not be used in any manner except under a
 * license agreement signed with Innov'ATM.
 */

import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { Box, IconButton, SelectChangeEvent, Tooltip } from '@mui/material';
import { isBefore } from 'date-fns';
import { useFormikContext } from 'formik';
import { memo, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ResourceType, useQueryGetGetResources } from '../../../../../../backend';
import { getTwoDaysAgo } from '../../../../../../utils/utils';
import { useRmsGanttValue } from '../../../RmsContext';
import { resourceFields } from '../../gantt.utils';
import { ParkingTowings } from './ParkingTowings/ParkingTowings';
import { ResourceSelect } from './ResourceSelect';
import {
    StyledArrivalResourcesBox,
    StyledDepartureResourcesBox,
    StyledOtherSelectBox,
    StyledResourcesSelectBox,
    StyledTerminalSelectBox,
} from './ResourcesSelect.style';
import { fields, TurnaroundFormValues } from './TurnaroundForm.utils';

export const ResourcesSelect = memo(function ResourcesSelect() {
    const { values, setValues } = useFormikContext<TurnaroundFormValues>();
    const resourceType = useRmsGanttValue('resourceType');
    const day = useRmsGanttValue('day');
    const isDayInPast = isBefore(day, getTwoDaysAgo());
    const isArrDayInPast =
        values?.turnaround?.arrivalFlight &&
        isBefore(new Date(values.turnaround.arrivalFlight.arrDay), getTwoDaysAgo());

    const { data: allResources } = useQueryGetGetResources({ includeNonActive: true });

    const [terminalLink, setTerminalLink] = useState<boolean>(
        values?.turnaround?.arrivalFlight?.arrivalTerminal === values?.turnaround?.departureFlight?.departureTerminal ||
            false,
    );

    const handleLinkedChange = useCallback(
        (event: SelectChangeEvent, type: ResourceType) => {
            setValues({
                ...values,
                turnaround: {
                    ...values.turnaround,
                    arrivalFlight: values.turnaround.arrivalFlight
                        ? {
                              ...values.turnaround.arrivalFlight,
                              [resourceFields[type].arrival]: event.target.value,
                          }
                        : null,
                    departureFlight: values.turnaround.departureFlight
                        ? {
                              ...values.turnaround.departureFlight,
                              [resourceFields[type].departure]: event.target.value,
                          }
                        : null,
                },
                resourceTypes: [...values.resourceTypes, type],
            });
        },
        [setValues, values],
    );

    const handleUnlinkedChange = useCallback(
        (event: SelectChangeEvent, type: ResourceType, arrivalField: boolean) => {
            if (arrivalField) {
                setValues({
                    ...values,
                    turnaround: {
                        ...values.turnaround,
                        arrivalFlight: {
                            ...values.turnaround.arrivalFlight,
                            [resourceFields[type].arrival]: event.target.value,
                        },
                    },
                    resourceTypes: [...values.resourceTypes, type],
                });
            } else {
                setValues({
                    ...values,
                    turnaround: {
                        ...values.turnaround,
                        departureFlight: {
                            ...values.turnaround.departureFlight,
                            [resourceFields[type].departure]: event.target.value,
                        },
                    },
                    resourceTypes: [...values.resourceTypes, type],
                });
            }
        },
        [setValues, values],
    );

    const handleTerminalLinkOption = useCallback(() => {
        //if link option is activated, set departure terminal with arrival terminal
        if (!terminalLink) {
            setValues({
                ...values,
                turnaround: {
                    ...values.turnaround,
                    departureFlight: {
                        ...values.turnaround.departureFlight,
                        departureTerminal: values.turnaround.arrivalFlight?.arrivalTerminal || '',
                    },
                },
                resourceTypes: [...values.resourceTypes, ResourceType.TERMINAL],
            });
        }
        setTerminalLink(!terminalLink);
    }, [setValues, terminalLink, values]);

    return (
        <StyledResourcesSelectBox>
            {(resourceType === ResourceType.PARKING || resourceType === ResourceType.TERMINAL) && (
                <>
                    <ParkingTowings
                        allResources={allResources}
                        disabled={isDayInPast}
                        isArrDayInPast={isArrDayInPast}
                    />
                    <StyledTerminalSelectBox id={'terminal'}>
                        {values.turnaround.arrivalFlight && (
                            <Box sx={{ width: '100%' }}>
                                <ResourceSelect
                                    selectedValue={values.turnaround.arrivalFlight.arrivalTerminal}
                                    isResourceLinked={terminalLink}
                                    resourceType={ResourceType.TERMINAL}
                                    isArrival={true}
                                    allResources={allResources}
                                    handleLinkedChange={handleLinkedChange}
                                    handleUnlinkedChange={handleUnlinkedChange}
                                    disabled={isDayInPast || isArrDayInPast}
                                />
                            </Box>
                        )}
                        {values.turnaround.arrivalFlight && values.turnaround.departureFlight && (
                            <Box>
                                <Tooltip
                                    title={
                                        terminalLink ? (
                                            <FormattedMessage id={'resourceForm.resource.linked'} />
                                        ) : (
                                            <FormattedMessage id={'resourceForm.terminal.unlinked'} />
                                        )
                                    }
                                >
                                    <IconButton
                                        sx={{ width: '30px', height: '30px', color: '#B6D2E5' }}
                                        aria-label="linked"
                                        component="label"
                                        onClick={handleTerminalLinkOption}
                                        disabled={isDayInPast}
                                    >
                                        {terminalLink ? <LinkIcon /> : <LinkOffIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                        {values.turnaround.departureFlight && (
                            <Box sx={{ width: '100%' }}>
                                <ResourceSelect
                                    selectedValue={values.turnaround.departureFlight.departureTerminal}
                                    isResourceLinked={terminalLink}
                                    resourceType={ResourceType.TERMINAL}
                                    isArrival={false}
                                    allResources={allResources}
                                    handleLinkedChange={handleLinkedChange}
                                    handleUnlinkedChange={handleUnlinkedChange}
                                    disabled={isDayInPast || (isArrDayInPast && terminalLink)}
                                />
                            </Box>
                        )}
                    </StyledTerminalSelectBox>
                </>
            )}
            <StyledOtherSelectBox id={'otherResources'}>
                {values.turnaround.arrivalFlight && (
                    <StyledArrivalResourcesBox isarrivalonly={values.turnaround.departureFlight ? 'false' : 'true'}>
                        {Object.keys(ResourceType).map(
                            type =>
                                fields.arrival[type] && (
                                    <ResourceSelect
                                        key={fields.arrival[type].field}
                                        selectedValue={values.turnaround.arrivalFlight[fields.arrival[type].field]}
                                        isResourceLinked={false}
                                        resourceType={fields.arrival[type].resourceType}
                                        isArrival={true}
                                        allResources={allResources}
                                        handleLinkedChange={handleLinkedChange}
                                        handleUnlinkedChange={handleUnlinkedChange}
                                        disabled={isDayInPast || isArrDayInPast}
                                        addLabelFlowSuffix={false}
                                    />
                                ),
                        )}
                    </StyledArrivalResourcesBox>
                )}
                {values.turnaround.departureFlight && (
                    <StyledDepartureResourcesBox isdepartureonly={values.turnaround.arrivalFlight ? 'false' : 'true'}>
                        {Object.keys(ResourceType).map(
                            type =>
                                fields.departure[type] && (
                                    <ResourceSelect
                                        key={fields.departure[type].field}
                                        selectedValue={values.turnaround.departureFlight[fields.departure[type].field]}
                                        isResourceLinked={false}
                                        resourceType={fields.departure[type].resourceType}
                                        isArrival={false}
                                        allResources={allResources}
                                        handleLinkedChange={handleLinkedChange}
                                        handleUnlinkedChange={handleUnlinkedChange}
                                        disabled={isDayInPast}
                                        addLabelFlowSuffix={false}
                                    />
                                ),
                        )}
                    </StyledDepartureResourcesBox>
                )}
            </StyledOtherSelectBox>
        </StyledResourcesSelectBox>
    );
});
